'use client';

import {
  cartLinePricing,
  cartSavings,
  cartSubtotal,
  cartTotal,
  overlayPromo,
  promotionalCopy,
  variantPricing,
} from '@/utils/pricing/Pricing';

import { createContext, use, useContext, useMemo } from 'react';

const PricingContext = createContext(null);

/** Builds the pricing API for one `settings` snapshot — plain function so callers can memoise it themselves. */
export function pricingApiFor(settings) {
  const pricingSettings = settings?.settings?.pricing;
  const pricesValidFrom = settings?.campaign?.prices_valid_from;
  const pricesValidTo = settings?.campaign?.prices_valid_to;

  // Merges the campaign validity window (from campaign_settings) into the
  // pricing strategy settings, so strategies can gate discounts on it.
  const discountSettings = pricingSettings && {
    ...pricingSettings,
    prices_valid_from: pricesValidFrom,
    prices_valid_to: pricesValidTo,
  };

  return {
    variant: (variant) => variantPricing(variant, discountSettings),
    cart: {
      line: (cartLine) => cartLinePricing(cartLine, discountSettings),
      total: (cart) => cartTotal(cart, discountSettings),
      subtotal: (cart) => cartSubtotal(cart, discountSettings),
      savings: (cart) => cartSavings(cart, discountSettings),
    },
    promotionalCopy: (variant = null, variation = 'normal') =>
      promotionalCopy(discountSettings, variation, variant),
    overlayPromo: (variant = null) => overlayPromo(discountSettings, variant),
  };
}

/**
 * Context Provider for the Pricing Engine.
 * Must wrap any components that need to calculate prices.
 * Carries the root settings promise — consumers unwrap it themselves via
 * `usePricing()` (suspends) or `usePricingSettingsPromise()` (does not).
 */
export const PricingProvider = ({ settingsPromise, children }) => (
  <PricingContext.Provider value={settingsPromise}>{children}</PricingContext.Provider>
);

/** Raw settings promise, for callers that must not suspend (e.g. `CartProvider`). */
export function usePricingSettingsPromise() {
  const settingsPromise = useContext(PricingContext);
  if (!settingsPromise) {
    throw new Error('usePricingSettingsPromise must be used within a PricingProvider');
  }
  return settingsPromise;
}

/**
 * Provides access to the centralized pricing engine.
 *
 * Developers should use this context to calculate prices, sub-totals, and discounts
 * across the site. The engine relies on the integer-cents arithmetic layer internally
 * to avoid floating-point drift, ensuring consistency between Shopify payloads and local state.
 *
 * @example
 * ```jsx
 * const pricing = usePricing();
 *
 * // 1. Calculate prices for a single variant (returns { full, sale, discount, hasPriceDifference })
 * const prices = pricing.variant(shopifyVariant);
 * console.log(prices.sale); // e.g., 999.00
 *
 * // 2. Get promotional copy configured in the CMS for a variant
 * const promoCopy = pricing.promotionalCopy(shopifyVariant, 'extended');
 *
 * // 3. Work with the entire cart or cart lines
 * const subtotal = pricing.cart.subtotal(cart); // Sum of full line prices
 * const total = pricing.cart.total(cart);       // Sum of sale line prices (what the user actually pays before tax)
 * const savings = pricing.cart.savings(cart);   // Total amount saved across the cart
 * const linePricing = pricing.cart.line(item);  // Returns { full, sale, discount, hasPriceDifference }
 *
 * // 4. Resolve the gallery promo overlay's code + message together
 * const { code, message } = pricing.overlayPromo(shopifyVariant);
 * ```
 */
export const usePricing = () => {
  const settings = use(usePricingSettingsPromise());
  return useMemo(() => pricingApiFor(settings), [settings]);
};
